import { useForm } from '@refinedev/core';
import { useNavigate, useParams } from 'react-router-dom';
import { Settings, ScrollText } from 'lucide-react';
import { FormHeader } from '@/components/forms';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TriggerWizard } from './components/TriggerWizard';
import { TriggerLogs } from './logs';
import type { Trigger, TriggerFormData } from './types';

/**
 * TriggersForm - форма управления триггерами
 *
 * Позволяет создавать и редактировать триггеры с пошаговым wizard.
 * В режиме редактирования добавляет таб с логами выполнения.
 *
 * @returns {React.ReactElement} Форма триггера
 */
export function TriggersForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const { formLoading, onFinish, query } = useForm<Trigger>({
    resource: 'triggers',
    action: isEdit ? 'edit' : 'create',
    id,
    redirect: 'list',
  });

  const trigger = query?.data?.data;
  const formKey = isEdit ? `edit-${trigger?.id ?? 'loading'}` : 'create';

  const handleSubmit = async (data: TriggerFormData) => {
    await onFinish(data);
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (!isEdit) {
    return (
      <TriggerWizard
        key={formKey}
        trigger={trigger}
        isLoading={formLoading}
        onSubmit={handleSubmit}
        onBack={handleBack}
      />
    );
  }

  return (
    <div className="space-y-6">
      <FormHeader
        title="Редактировать триггер"
        onBack={handleBack}
        sticky
      />

      <Tabs defaultValue="settings">
        <TabsList>
          <TabsTrigger value="settings" className="gap-2">
            <Settings className="h-4 w-4" />
            Настройка
          </TabsTrigger>
          <TabsTrigger value="logs" className="gap-2">
            <ScrollText className="h-4 w-4" />
            Логи выполнения
          </TabsTrigger>
        </TabsList>

        <TabsContent value="settings" forceMount className="mt-6 data-[state=inactive]:hidden">
          <TriggerWizard
            key={formKey}
            trigger={trigger}
            isLoading={formLoading}
            onSubmit={handleSubmit}
            onBack={handleBack}
            showHeader={false}
          />
        </TabsContent>

        <TabsContent value="logs" forceMount className="mt-6 data-[state=inactive]:hidden">
          {id && trigger && (
            <TriggerLogs triggerId={id} triggerName={trigger.name} />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
